import type { CameraUploadPayload } from '@/types/camera';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';
import { Filesystem } from '@capacitor/filesystem';
import { useToast } from 'primevue/usetoast';
import { useI18n } from 'vue-i18n';

export function useCamera() {
  const toast = useToast();
  const { t } = useI18n();

  // Request permissions for mobile
  async function requestPermissions(): Promise<boolean> {
    if (Capacitor.isNativePlatform()) {
      try {
        const permissions = await Camera.requestPermissions({
          permissions: ['camera', 'photos'],
        });

        if (permissions.camera === 'denied' || permissions.photos === 'denied') {
          toast.add({
            severity: 'error',
            summary: t('common.error'),
            detail: t('permissions.camera_denied'),
            life: 3000,
          });
          return false;
        }
        return true;
      } catch (error) {
        console.error('Permission error:', error);
        toast.add({
          severity: 'error',
          summary: t('common.error'),
          detail: t('permissions.error'),
          life: 3000,
        });
        return false;
      }
    }
    return true;
  }

  async function convertCapacitorImageToFile(
    image: { path?: string; webPath?: string; format?: string },
    filename: string = 'image.jpg',
  ): Promise<File> {
    if (Capacitor.isNativePlatform() && image.path) {
      // Lire le fichier depuis le Filesystem (mobile)
      const fileData = await Filesystem.readFile({ path: image.path });

      const byteCharacters = atob(fileData.data as string);
      const byteNumbers = Array.from(byteCharacters).map((c) => c.charCodeAt(0));
      const byteArray = new Uint8Array(byteNumbers);

      let mimeType = 'image/jpeg';
      if (image.format) {
        mimeType = `image/${image.format.toLowerCase()}`;
      }

      return new File([byteArray], filename, { type: mimeType });
    }

    if (image.webPath) {
      // Fallback web → récupérer le blob
      const response = await fetch(image.webPath);
      const blob = await response.blob();
      return new File([blob], filename, { type: blob.type || 'image/jpeg' });
    }

    throw new Error('No valid image path found');
  }

  async function takePhoto(): Promise<CameraUploadPayload | null> {
    try {
      const hasPermission = await requestPermissions();
      if (!hasPermission) return null;

      const image = await Camera.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType: CameraResultType.Uri,
        source: CameraSource.Camera,
      });

      const filename = `camera_${Date.now()}.jpg`;
      const file = await convertCapacitorImageToFile(image, filename);

      return {
        type: 'camera',
        file,
        name: file.name,
        mimeType: file.type,
        size: file.size,
      };
    } catch (error) {
      console.error('Camera error:', error);
      if (error instanceof Error && error.message.includes('cancelled')) return null;

      toast.add({
        severity: 'error',
        summary: t('common.error'),
        detail: t('camera.error_taking_photo'),
        life: 3000,
      });
      return null;
    }
  }

  async function selectFromGallery(): Promise<CameraUploadPayload | null> {
    try {
      const hasPermission = await requestPermissions();
      if (!hasPermission) return null;

      const image = await Camera.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType: CameraResultType.Uri,
        source: CameraSource.Photos,
      });

      const filename = `gallery_${Date.now()}.jpg`;
      const file = await convertCapacitorImageToFile(image, filename);

      return {
        type: 'gallery',
        file,
        name: file.name,
        mimeType: file.type,
        size: file.size,
      };
    } catch (error) {
      console.error('Gallery error:', error);
      if (
        typeof error === 'object' &&
        error !== null &&
        'message' in error &&
        typeof (error as { message: unknown }).message === 'string' &&
        (error as { message: string }).message.includes('cancelled')
      ) {
        return null;
      }

      toast.add({
        severity: 'error',
        summary: t('common.error'),
        detail: t('gallery.error_selecting_image'),
        life: 3000,
      });
      return null;
    }
  }
  return {
    takePhoto,
    selectFromGallery,
    requestPermissions,
  };
}
